"use client";

import React, { useCallback, useEffect, useState, useRef } from "react";
import Layout4 from "../../DataLayouts/Layout4";
import StoreIcon from "@mui/icons-material/Store";
import PersonIcon from "@mui/icons-material/Person";
import { fetchOrganizationDepartment } from "../../../Apis/Department-api";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import toast from "react-hot-toast";
import { MAIN_URL } from "../../../Configurations/Urls";
import usePermissionDataStore from "../../../Zustand/Store/usePermissionDataStore";
import TableDataGeneric from "../../../Configurations/TableDataGeneric";
import { useNavigate, useSearchParams } from "react-router-dom";
import { operatorRequiresValue } from "../../../Components/filters/operators";

const DEFAULT_COLUMNS = [
  {
    field: "department_name",
    label: "Department Name",
    visible: true,
    width: 200,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_short_name",
    label: "Short Name",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_mail_id",
    label: "Email",
    visible: true,
    width: 220,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "department_employees_count",
    label: "Employees Count",
    visible: true,
    width: 150,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
  {
    field: "description",
    label: "Description",
    visible: true,
    width: 250,
    filterable: true,
    sortable: true,
    pinned: "none",
    required: false,
  },
];

const DEFAULT_PAGINATION = {
  page: 1,
  pageSize: 10,
  total: 0,
  totalPages: 1,
  hasNext: false,
  hasPrev: false,
};

const DEFAULT_URL_PARAMS = {
  page: "1",
  pageSize: "10",
  sortBy: "organization_department_id",
  sortOrder: "desc",
};

// Helper functions
const buildFiltersFromParams = (searchParams) => {
  const filters = [];
  const processedFields = new Set();

  searchParams.forEach((value, key) => {
    if (
      key.endsWith("_operator") ||
      key.endsWith("_logic") ||
      !key.startsWith("filter_")
    )
      return;

    const field = key.replace("filter_", "");
    if (processedFields.has(field)) return;
    processedFields.add(field);

    const operator = searchParams.get(`filter_${field}_operator`) || "contains";
    const requiresValue = operatorRequiresValue(operator);

    if (!requiresValue || value) {
      let filterValue = requiresValue ? value : null;

      if (requiresValue && filterValue) {
        if (["equals", "not_equals"].includes(operator)) {
          filterValue = filterValue
            .split(",")
            .map((v) => v.trim().toLowerCase());
        } else if (["between", "not_between"].includes(operator)) {
          filterValue = filterValue.split(",").map((v) => v.trim());
        }
      }

      filters.push({ field, operator, value: filterValue });
    }
  });

  return filters;
};

function OrganizationDepartmentsList() {
  const { userData } = useAuthStore();
  const { Permission, setPermission } = usePermissionDataStore();
  const org = userData?.organization;
  const [loading, setLoading] = useState(true);
  const [departments, setDepartments] = useState([]);
  const [tableConfig, setTableConfig] = useState(null);
  const [configColumns, setConfigColumns] = useState(DEFAULT_COLUMNS);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [paginationData, setPaginationData] = useState(DEFAULT_PAGINATION);

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const fetchControllerRef = useRef(null);
  const hasInitializedURL = useRef(false);

  // Initialize URL parameters
  useEffect(() => {
    if (hasInitializedURL.current) return;

    const params = new URLSearchParams(searchParams);
    let needsUpdate = false;

    Object.entries(DEFAULT_URL_PARAMS).forEach(([key, defaultValue]) => {
      if (!params.has(key)) {
        params.set(key, defaultValue);
        needsUpdate = true;
      }
    });

    if (needsUpdate) {
      setSearchParams(params, { replace: true });
    }

    hasInitializedURL.current = true;
  }, [searchParams, setSearchParams]);

  // Load departments data
  useEffect(() => {
    if (!org?.organization_id || !hasInitializedURL.current) return;

    fetchControllerRef.current?.abort();
    fetchControllerRef.current = new AbortController();

    setLoading(true);

    const fetchData = async () => {
      try {
        const filters = buildFiltersFromParams(searchParams);
        const search = searchParams.get("search");
        const requestBody = {
          filters,
          page: Number.parseInt(searchParams.get("page"), 10) || 1,
          pageSize: Number.parseInt(searchParams.get("pageSize"), 10) || 10,
          sort: {
            field: searchParams.get("sortBy") || "organization_department_id",
            direction: searchParams.get("sortOrder") || "desc",
          },
          ...(search && search.trim() !== "" && { search: search.trim() }),
        };

        // Try new POST endpoint first, fallback to old GET endpoint if needed
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org.organization_id}/departments-filtered`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(requestBody),
            signal: fetchControllerRef.current.signal,
          }
        );

        let data;
        if (response.ok) {
          const result = await response.json();
          if (result.success && result.data) {
            data = result.data;
            
            if (result.pagination) {
              const pageSize =
                result.pagination.pageSize || result.pagination.limit || 10;
              setPaginationData({
                page:
                  result.pagination.page ||
                  Number.parseInt(searchParams.get("page"), 10) ||
                  1,
                pageSize,
                total: result.pagination.total || 0,
                totalPages: result.pagination.totalPages || 1,
                hasNext: result.pagination.hasNext || false,
                hasPrev: result.pagination.hasPrev || false,
              });
            }
          } else {
            data = [];
            setPaginationData(DEFAULT_PAGINATION);
          }
        } else if (response.status === 404) {
          // Fallback to old GET endpoint if POST endpoint doesn't exist yet
          const oldData = await fetchOrganizationDepartment(org.organization_id);
          data = oldData?.Departments || [];
          setPaginationData(DEFAULT_PAGINATION);
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        // Transform data - preserve existing transformation logic
        let b = data.map((item) => ({
          ...item,
          id: item.organization_department_id,
          department_name: item.department_name || "",
          department_mail_id: item.department_mail_id || "",
          department_short_name: item.department_short_name || "",
          description: item.description || "",
          department_employees_count: item.department_employees_count || 0,
        }));
        setDepartments(b);
      } catch (err) {
        if (err.name === "AbortError") return;
        
        if (err.response && err.response.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          console.error("Failed to fetch departments:", err);
          toast.error("Failed to fetch departments");
          setDepartments([]);
          setPaginationData(DEFAULT_PAGINATION);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      fetchControllerRef.current?.abort();
    };
  }, [org, searchParams]);

  // Load table configuration from general-datagrids API
  useEffect(() => {
    const loadTableConfiguration = async () => {
      if (!org?.organization_id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const configRes = await fetch(
            `${MAIN_URL}/api/general-datagrids`,
          {
            method: "GET",
            headers: { "Content-Type": "application/json" },
          }
        );

        if (configRes.ok) {
          const configResponse = await configRes.json();
          const datagrids = configResponse.datagrids || [];
          const orgKey = `departments_grid_${org.organization_id}`;
          const savedConfig = datagrids.find((dg) => dg.datagrid_key === orgKey);

          if (savedConfig) {
            const serverCfg = savedConfig.datagrid_default_configuration;
            setTableConfig(serverCfg);

            if (
              serverCfg?.columns &&
              Array.isArray(serverCfg.columns) &&
              serverCfg.columns?.length > 0
            ) {
              setConfigColumns(serverCfg.columns);
            } else {
              setConfigColumns(DEFAULT_COLUMNS);
            }
          } else {
            setConfigColumns(DEFAULT_COLUMNS);
          }
        }
      } catch (error) {
        console.error("Error loading table configuration:", error);
        setConfigColumns(DEFAULT_COLUMNS);
      } finally {
        setLoadingConfig(false);
      }
    };

    loadTableConfiguration();
  }, [org?.organization_id]);

  const deletedepartment = useCallback(
    async (id) => {
      try {
        const response = await fetch(
          `${MAIN_URL}/api/organizations/${org?.organization_id}/department/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          toast.success(data.message || "Department deleted successfully");
          // Refresh data after deletion by triggering useEffect
          setSearchParams((prev) => new URLSearchParams(prev));
        } else {
          const status = response.status;
          if (status === 401) {
            toast.error("Session Expired!");
            window.location.href = "/login";
          } else if (status === 422) {
            const firstError =
              data.errors?.[Object.keys(data.errors || {})[0]]?.[0] ||
              "Validation failed.";
            toast.error(firstError);
          } else if (status === 409) {
            toast.error(data.message || data.error || "This department is in use and cannot be deleted.");
          } else {
            toast.error(data.message || data.error || "Failed to delete department");
          }
        }
      } catch (error) {
        if (error.response?.status === 401) {
          toast.error("Session Expired!");
          window.location.href = "/login";
        } else {
          toast.error(
            error.response?.data?.error || "Failed to delete department"
          );
        }
        console.error("Delete failed:", error);
      }
    },
    [org?.organization_id, setSearchParams]
  );

  const handleEdit = useCallback(
    (item) => {
      navigate(
        `/organization/departments/edit/${item.id || item.organization_department_id}`
      );
    },
    [navigate]
  );

  const handleShow = useCallback(
    (item) => {
      navigate(
        `/organization/departments/view/${item.id || item.organization_department_id}`
      );
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (field, direction) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("sortBy", field);
        next.set("sortOrder", direction);
        next.set("page", "1");
        return next;
      });
    },
    [setSearchParams]
  );

  const handleFilterChange = useCallback(
    () => {
      setSearchParams((prev) => new URLSearchParams(prev));
    },
    [setSearchParams]
  );

  return (
    <>
      <Layout4
        loading={loading}
        heading={"Departments"}
        btnName={"Add Departments"}
        Data={departments}
        add_action={"DEPARTMENT_ADD"}
        delete_action={"DEPARTMENT_DELETE"}
        Icons={[
          <StoreIcon sx={{ fontSize: 60, color: "grey.500", mb: 2 }} />,
          <StoreIcon color="primary" />,
          <PersonIcon sx={{ color: "text.secondary" }} />,
        ]}
        messages={[
          "departments",
          "organisation departments",
          "Add Departments",
          "department, Deleting this department will also remove all related designations and employees",
        ]}
        Route={"/organization/departments"}
        setData={setDepartments}
        DeleteFunc={deletedepartment}
      />

      {!loadingConfig && (
        <TableDataGeneric
          tableName="departments"
          primaryKey="organization_department_id"
          heading="Departments"
          data={departments}
          sortname="organization_department_id"
          showActions={true}
          Route="/organization/departments"
          DeleteFunc={deletedepartment}
          EditFunc={handleEdit}
          handleShow={handleShow}
          onSortChange={handleSortChange}
          onFilterChange={handleFilterChange}
          edit_delete_action={["DEPARTMENT_EDIT", "DEPARTMENT_DELETE"]}
          token={localStorage.getItem("token")}
          configss={configColumns}
          paginationData={paginationData}
          filterModule="departments"
          {...(tableConfig && { config: tableConfig })}
        />
      )}
    </>
  );
}

export default OrganizationDepartmentsList;
